/**
 * Echo Hunt -- the fake-bullet generator prompt (P6, §5).
 *
 * VISIBILITY-MATRIX ROW (§16.0): "Fake generator".
 *  - knows real/fake?      inherent -- it is the step that writes fakes.
 *  - sees world/character?  NO. This is invariant #4: a fake written "for" the world would fit it
 *                           natively while a real bullet is only later adapted to it, and that
 *                           asymmetry would be a tell the user could learn.
 *  - sees other bullets?    NO (§5.1). Independent generation is what prevents "the fakes cohere with
 *                           each other but the real bullets don't" (§17 asymmetric coherence).
 *  - sees corpus?           NO. Not even the typicality cap, which is P12 and runs in plain code
 *                           OUTSIDE this prompt.
 *
 * The prompt therefore receives exactly four things: the CONCEPT SEED(s), the target EVENT TYPE, the
 * target LENGTH BAND, and the style rules (house style + cliché blacklist).
 *
 * ## The seeding rule (§5.2)
 *
 * The seed words are handed over only to be CONVERTED INTO CONCEPTS, and the model is explicitly
 * banned from using the words themselves. The concept must shape the SITUATION (the domain, the
 * problem, the activity), not appear as an object: "lantern" should become an event about darkness or
 * finding your way, never "a lamp". Without that rule, unusual objects would show up in fakes exactly
 * when they were seeded, and "a 3rd unexpected object" would become a rule for spotting fakes.
 *
 * ## Literal events only
 *
 * §5.2 also requires a concrete, literal event with no thematic or lyrical abstraction -- not because
 * abstraction is unrealistic, but because it is a LARGE language model's default register, so leaving
 * it allowed makes fakes cluster in one recognizable voice (§5.5, §17).
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §5, §16.0.
 */
import {JSONSchemaLike} from "../LLM.js";
import {BulletEventType} from "../Types.js";
import {EVENT_TYPE_DESCRIPTIONS, HOUSE_STYLE_RULES} from "./IndexPrompt.js";
import type {FakeLengthBand} from "../FakeGenerator.js";

/** Anti-cliché rules stated in every generator prompt (§5.5). Deliberately phrased as prohibitions of
 * the recognizable LLM-dream register, because that register -- not any single word -- is the tell. */
export const FAKE_CLICHE_RULES = [
	"No glowing, humming, shimmering or ancient anything; no mysterious symbols; no destiny or \"something was calling to me\".",
	"No tidy symbolism and no neat cause-and-effect: no event arranged so that it \"means\" something.",
	"No emotional resolution, no epiphany, no \"and I understood\".",
	"No abstract or lyrical phrasing; state concrete physical events.",
	"No dream framing of any kind: never say dream, sleep, wake, lucid, nightmare, vision.",
	"Do not explain why anything happened; waking recollection is often unexplained.",
];

export function GenerateFake_SystemPrompt(): string {
	return [
		"You write short, plain, first-person past-tense recollections of an event.",
		"You write them as if they genuinely happened to you, in the same voice as an ordinary private journal.",
		"Reply with JSON only. No prose, no code fences, no explanation.",
	].join("\n");
}

export type GenerateFake_UserPromptOptions = {
	/** 1-2 words whose CONCEPTS must shape the situation -- never to be used themselves (§5.2) */
	seedWords: string[];
	/** how many of the seed words' concepts must be used (1..2) */
	conceptCount: number;
	/** the event type this fake must be, drawn from the real corpus's distribution (§5.3) */
	eventType: BulletEventType;
	/** target length, drawn from the real corpus's own word-count band */
	lengthBand: FakeLengthBand;
	/** cliché phrases to avoid (§5.5) */
	blacklist?: string[]|n;
	/** the user's banned terms (§4.4), restated here so a fake cannot smuggle one in */
	bannedWords?: string[]|n;
	/** on a retry: why the previous candidate was rejected */
	fixNotes?: string|n;
};

export function GenerateFake_UserPrompt(opts: GenerateFake_UserPromptOptions): string {
	const {seedWords, conceptCount, eventType, lengthBand, blacklist, bannedWords, fixNotes} = opts;

	const parts = [
		"## Task",
		"Write ONE bullet describing a single remembered event, in the first person, past tense.",
		`It must be ${lengthBand.minWords}-${lengthBand.maxWords} words long.`,
		"",
		"## The kind of event",
		`The event's core must be: ${EVENT_TYPE_DESCRIPTIONS[eventType] ?? eventType}.`,
		"",
		"## The concepts you must build the situation around",
		...seedWords.map(word=>`- ${word}`),
		"",
		`Use the CONCEPT behind ${conceptCount == 1 ? "this word" : "these words"} -- ${conceptCount == 1 ? "its" : "their"} domain, problem or activity -- to shape WHAT HAPPENS.`,
		"",
		"RULES for the concepts:",
		"- The concept must shape the SITUATION, not appear as an object.",
		"  Example: the concept behind \"lantern\" could become an event about being in darkness, or about finding your way.",
		"  Counter-example (wrong): putting a lamp, a light or a lantern in the scene.",
		"- NEVER use the seed word itself, any inflection of it, or an obvious stand-in for it.",
		"- The event must still make sense on its own, with nothing left to explain.",
		"",
		"## What to write",
		"- A concrete, literal event: physical actions, objects, places and people.",
		"- Ordinary register. A waking person's plain recollection, not a story written for an audience.",
		"- It is fine for the event to be strange or impossible. Do NOT remark on it, and do not resolve it.",
		"",
		"## Do not do any of this",
		...FAKE_CLICHE_RULES.map(rule=>`- ${rule}`),
	];

	if ((blacklist ?? []).length > 0) {
		parts.push(
			"",
			"## Phrases that are completely forbidden",
			"These are known machine-written tells. Do not use them, and do not write their equivalents.",
			...(blacklist ?? []).map(phrase=>`- ${phrase}`),
		);
	}
	if ((bannedWords ?? []).length > 0) {
		parts.push(
			"",
			"## Terms the user excluded",
			"Do not use these, any inflection of them, or an obvious stand-in.",
			...(bannedWords ?? []).map(word=>`- ${word}`),
		);
	}

	parts.push(
		"",
		"## House style",
		HOUSE_STYLE_RULES,
		"",
		`## Output`,
		`Reply with ONE JSON object: {"text": "..."}`,
		"The `text` value is the bullet itself -- one event, no heading, no bullet character, no quotation marks.",
	);

	if (fixNotes != null && fixNotes.trim().length > 0) {
		parts.push(
			"",
			"## Fix this problem from your previous attempt",
			fixNotes.trim(),
		);
	}

	return parts.join("\n");
}

/** The JSON shape `CallLLM` validates each attempt against. Loose by design: the semantic checks
 * (seed leakage, house style, length) happen in `FakeGenerator.ts`, where a failure can be explained
 * to the model in plain language. */
export function GenerateFake_JSONSchema(): JSONSchemaLike {
	return {
		type: "object",
		required: ["text"],
		properties: {
			text: {type: "string", minLength: 1},
		},
	};
}
